//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import {
  UseMutationResult,
  useIsMutating,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useDispatch, useSelector, useStore } from 'react-redux';

import { removeFromCollection } from '@apis/bulkActionsApi';
import {
  shareModerationMedia,
  unshareModerationMedia,
} from '@apis/moderation/moderationApi';
import { clearItems } from '@app/bulk-actions/bulk-actions-actions/action-creators';
import { BulkActionNames } from '@app/bulk-actions/model/BulkActionNames';
import { ISelectedItem } from '@app/bulk-actions/model/ISelectedItem';
import { getModeratorBulkActionsAccess } from '@app/bulk-actions/model/moderatorBulkActionsAccess';
import { getBulkActionsSelectedItems } from '@app/bulk-actions/selectors/bulk-actions-selector';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import { MediaLibraryQueryParams } from '@media-library/constants/mediaLibraryConstants';
import { ICuralateStore } from '@models/AppState';
import ClientId from '@models/ClientId';
import Cuid from '@models/Cuid';
import { SessionMode } from '@moderation-mode/constants/SessionMode';
import { sessionModeStore } from '@moderation-mode/sessionModeStore';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import {
  getStateName,
  getStateParams,
} from '@react-bridge/wrappers/navigation';
import { getClientId } from '@redux/selectors/getClientId';
import { pluralize } from '@utils/stringUtils';

export interface ModeratorBulkScope {
  clientId: ClientId;
  collectionId?: string;
  stateName: string;
}

export type ModeratorBulkMediaAction =
  | 'share'
  | 'request-review'
  | 'unshare'
  | 'remove-from-stream';

interface ModeratorBulkMediaVariables {
  action: ModeratorBulkMediaAction;
  cuids: Cuid[];
  scope: ModeratorBulkScope;
}

interface ModeratorBulkMediaMutationContext { submittedSelection: ISelectedItem[] }

export interface ModeratorBulkMediaMutationResult {
  isPending: boolean;
  hasPendingBulkAction: () => boolean;
  mutateAsync: UseMutationResult<
    void,
    Error,
    ModeratorBulkMediaVariables,
    ModeratorBulkMediaMutationContext
  >['mutateAsync'];
}

const isCurrentScope = (
  scope: ModeratorBulkScope,
  state: ICuralateStore,
): boolean =>
  getClientId(state) === scope.clientId &&
  String(getStateName()) === scope.stateName &&
  getStateParams<MediaLibraryQueryParams>().collectionId === scope.collectionId;

// Re-read permission, flags and view when executing, not just when rendering.
export const hasCurrentModeratorBulkWriteAccess = (
  scope: ModeratorBulkScope,
  state: ICuralateStore,
): boolean =>
  isCurrentScope(scope, state) &&
  getModeratorBulkActionsAccess(
    state.session,
    sessionModeStore.getMode() === SessionMode.Moderator,
    getStateName(),
  ).canWrite;

export const useModeratorBulkMediaMutation = (): ModeratorBulkMediaMutationResult => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const store = useStore<ICuralateStore>();
  const clientId = useSelector(getClientId);
  const mutationKey = [
    QueryKeyConstants.MediaLibrary,
    QueryKeyConstants.MediaLibraryModeratorBulkMedia,
    clientId,
  ];
  const mutationFilters = { mutationKey, exact: true };
  const isBulkPending = useIsMutating(mutationFilters) > 0;

  const mutation = useMutation({
    mutationKey,
    mutationFn: async ({
      action,
      cuids,
      scope,
    }: ModeratorBulkMediaVariables): Promise<void> => {
      const liveState = store.getState();
      const access = getModeratorBulkActionsAccess(
        liveState.session,
        sessionModeStore.getMode() === SessionMode.Moderator,
        getStateName(),
      );

      if (
        !hasCurrentModeratorBulkWriteAccess(scope, liveState) ||
        cuids.length === 0 ||
        (action === 'share' && !access.isStream) ||
        (action === 'unshare' && !access.isAllSharedMedia)
      ) {
        throw new Error('This moderation action is no longer available.');
      }

      if (action === 'remove-from-stream') {
        const collectionId = scope.collectionId;

        if (
          !access.isStream ||
          !collectionId ||
          liveState.bulkActions.currentCollection?.id !== collectionId ||
          liveState.bulkActions.disabledActions.includes(
            BulkActionNames.REMOVE_FROM_COLLECTION,
          )
        ) {
          throw new Error('This stream removal is no longer available.');
        }

        const requestedCuids = cuids.map((cuid) => Cuid.toPartial(cuid));

        const response = await removeFromCollection(
          requestedCuids,
          collectionId,
        );

        const removedCuids = new Set(response.cuids);

        if (!requestedCuids.every((cuid) => removedCuids.has(cuid))) {
          throw new Error('Not all selected items were removed.');
        }

        return;
      }

      if (action === 'unshare') {
        await unshareModerationMedia({ cuids });
      } else {
        await shareModerationMedia({
          cuids,
          isForceReviewed: action === 'request-review',
        });
      }
    },
    onError: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Error',
          message:
            'Unable to complete this action. Please try again or contact Bazaarvoice Support.',
          type: ToastType.ERROR,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
    onMutate: () => ({
      submittedSelection: getBulkActionsSelectedItems(store.getState()),
    }),
    onSettled: (_data, _error, { cuids }) => {
      void queryClient.invalidateQueries({
        queryKey: [
          QueryKeyConstants.MediaFilters,
          QueryKeyConstants.MediaFiltersMediaItems,
        ],
      });
      cuids.forEach((cuid) => {
        void queryClient.invalidateQueries({
          queryKey: [
            QueryKeyConstants.UniversalImageActions,
            QueryKeyConstants.UniversalImageActionsRights,
            QueryKeyConstants.UniversalImageActionsMetadata,
            cuid,
          ],
        });
      });
    },
    onSuccess: (_data, { action, cuids, scope }, context) => {
      // Do not clear a different page/client's selection after navigation,
      // or a new selection made while the request was in flight.
      const liveState = store.getState();
      if (
        isCurrentScope(scope, liveState) &&
        getBulkActionsSelectedItems(liveState) === context.submittedSelection
      ) {
        dispatch(clearItems());
      }

      const items = pluralize(cuids.length, 'item');
      let message: string;

      if (action === 'remove-from-stream') {
        message = `Removed ${items} from the stream.`;
      } else if (action === 'unshare') {
        message = `Removed ${items} from All Shared Media.`;
      } else if (action === 'request-review') {
        message = `Requested review for ${items}.`;
      } else {
        message = `Shared ${items}.`;
      }

      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Success!',
          message,
          type: ToastType.SUCCESS,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
    retry: false,
  });

  return {
    ...mutation,
    isPending: mutation.isPending || isBulkPending,
    hasPendingBulkAction: () =>
      queryClient.isMutating(mutationFilters) > 0,
  };
};
